import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { X } from 'lucide-react';

import type { ConsoleConfig, ConsoleEnvironment } from '../config/consoleConfig';
import { setActiveManagerBaseUrl } from '../api/client';
import { isForbiddenTenantError } from '../api/whoami';

// FD-421 / FD-ADR-040 §7 — which environment the console is acting on.
//
// A fleet console lists several AirBridge deployments and serves them from one
// bundle and one MSAL instance (FD-420). This module decides which one is ACTIVE,
// and everything that follows from that: the Manager origin every request is sent
// to, and the query cache those responses land in.
//
// The hazard this is built around is not "the operator cannot switch". It is
// **acting on production believing you are on staging** — the hazard `TopBar.tsx`
// named when it refused to ship a mock dropdown, and the one FD-ADR-040 §8 layers
// four defences against. Three of them live here or next door: the selection is
// in the URL where it is visible and shareable, an unrecognised id says so rather
// than quietly serving something else, and switching throws the cache away.

/** The query-string key. One name, used by the provider and the tests. */
export const ENV_SEARCH_PARAM = 'env';

/**
 * What the console knows about whether it may reach an environment (FD-422).
 *
 * THREE STATES, NOT TWO, and the third is the important one. `unknown` is the
 * starting value and the resting place of every failure that is not a tenant
 * refusal — a 500, a timeout, a CORS error, a Manager that is down. Only
 * `forbidden` HIDES (FD-586; it greyed under FD-422), so a transient probe
 * failure degrades to "still listed" rather than to a console that looks like a
 * total loss of access.
 */
export type EnvironmentReachability = 'unknown' | 'reachable' | 'forbidden';

export interface EnvironmentHealth {
  reachability: EnvironmentReachability;
  /**
   * The environment's OWN `ENVIRONMENT_NAME`, as its Manager reported it — the
   * input to the drift check. Null until a probe answers, and null forever on a
   * Manager that does not emit the setting. Absence is not disagreement.
   */
  environmentName: string | null;
  /**
   * The display label the Manager reported (FD-586 `environmentLabel`). Only
   * consulted when `config.json` carries no label — see `resolveEnvironment`.
   */
  environmentLabel: string | null;
  /**
   * The Manager's `ADMIN_AUTH_MODE` as `GET /v1/whoami` reported it
   * (`single-tenant` | `multi-tenant`), or null until a probe answers, after a
   * failed probe, or on a Manager that does not emit it.
   *
   * FD-594 — read by the new-campaign wizard to decide whether a Hybrid campaign
   * may fall back to "this deployment's own Entra tenant". On a hosted
   * (multi-tenant) environment that tenant is Rubix's, not the customer's, so the
   * default is not offered. PRESENTATION ONLY: the server refuses the same request
   * with `400 DESTINATION_TENANT_REQUIRED`, and that refusal is the control.
   */
  adminAuthMode: string | null;
  /**
   * Whether the console has its ANSWER for this environment (FD-586).
   *
   * Separate from `reachability` because `unknown` means two different things
   * that the landing gate must tell apart: "nobody has replied yet" (wait) and
   * "the probe failed for a reason that is not a tenant refusal, or never
   * replied in time" (stop waiting, and let the page show its own error). The
   * second is SETTLED-unknown — still listed, still not forbidden, but no longer
   * something worth holding a loader for. Folding it into reachability as a
   * fourth value would make every existing `=== 'unknown'` reading wrong in one
   * direction or the other, which is the kind of change that passes review.
   */
  probed: boolean;
}

/**
 * What the probe (or a test standing in for it) reports for one environment.
 *
 * The provider stamps `probed: true` itself: anything arriving through
 * `recordEnvironmentProbe` IS an answer, so making callers say so would only
 * create a way to say it wrongly.
 */
export interface EnvironmentProbeResult {
  reachability: 'reachable' | 'forbidden';
  environmentName: string | null;
  environmentLabel?: string | null;
  adminAuthMode?: string | null;
}

const UNKNOWN_HEALTH: EnvironmentHealth = {
  reachability: 'unknown',
  environmentName: null,
  environmentLabel: null,
  adminAuthMode: null,
  probed: false,
};

/** Where an environment's display label came from. Drives the drift check. */
export type EnvironmentLabelSource = 'config' | 'server' | 'id';

/**
 * An environment as the rest of the console sees it: the config entry with its
 * display label RESOLVED (FD-586).
 *
 * Resolved here, once, rather than with `?? id` sprinkled through every
 * component that prints a label — ten-plus call sites each choosing their own
 * fallback is how a selector says "APC" while the echo in the dialog below it
 * says "apc". Every consumer that read `environment.label` before FD-586 keeps
 * reading a string and needs no change.
 */
export interface ResolvedEnvironment extends ConsoleEnvironment {
  label: string;
  labelSource: EnvironmentLabelSource;
}

/**
 * config `label` → the Manager's own `environmentLabel` → the id.
 *
 * Config wins because it is the deployer's deliberate choice and is available
 * before any probe answers; the server label exists for the fleets that cannot
 * put a name in a public file. The id is the floor — it is what an operator
 * would type into `?env=`, so it is never a wrong thing to show.
 */
export function resolveEnvironment(
  environment: ConsoleEnvironment,
  health: EnvironmentHealth | undefined,
): ResolvedEnvironment {
  if (environment.label) {
    return { ...environment, label: environment.label, labelSource: 'config' };
  }
  const serverLabel = health?.environmentLabel?.trim();
  if (serverLabel) {
    return { ...environment, label: serverLabel, labelSource: 'server' };
  }
  return { ...environment, label: environment.id, labelSource: 'id' };
}

/**
 * The longest the landing gate holds the loader waiting for probes (FD-586).
 *
 * Long enough for a Consumption-plan Manager waking from cold to answer
 * `whoami`; short enough that a probe that never settles — a request the browser
 * keeps open — cannot hang the console. Past it every unanswered environment is
 * treated as settled-unknown and the page renders, showing its own error if
 * there is one. That is the RouteLoader MAX_MS bargain applied to landing: an
 * animation that never resolves is worse than an honest error.
 *
 * FD-596 — RAISED FROM 10 s TO 60 s, because 10 s did not meet this comment's
 * own first sentence. Measured on `apc` (2026-10-23), a cold Manager spends
 * ~16 s in `profile.ps1` alone before any function runs, so `whoami` answers in
 * ~17–20 s cold, and the table-reading pages behind it in 40–57 s. With 10 s the
 * gate opened BEFORE the active environment had answered: on the Air Bridge
 * fleet that is `environments[0]`, Rubix's own, so a customer admin's page
 * mounted there, waited out a second cold start, and was refused — the exact
 * FD-586 landing this gate exists to prevent, reintroduced by a cold fleet. 60 s
 * clears the measured worst case with margin, and `SlowLoadNotice` tells the
 * operator why they are waiting once the hold passes 5 s.
 */
export const PROBE_SETTLE_TIMEOUT_MS = 60_000;

function normalise(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Does this console-side name agree with the name the server reported?
 *
 * Tolerant on purpose, in one direction only. The comparison is between a value
 * an operator typed into a fleet inventory ("Rubix staging", "Production") and a
 * Bicep parameter ("staging", "prod"), and those are expected to differ in
 * register and in length while naming the same deployment. A banner that fires
 * on every console is a banner nobody reads, and then the real one is invisible.
 *
 * So agreement is: equal, or a prefix (`Production` for `prod`), or one of the
 * label's words (`Rubix staging` for `staging`). The prefix rule requires three
 * characters so a two-letter server name cannot agree with half the fleet.
 */
function agrees(candidate: string | undefined, serverName: string): boolean {
  if (!candidate) return false;
  const a = normalise(candidate);
  const b = normalise(serverName);
  if (a === '' || b === '') return false;
  if (a === b) return true;
  if (b.length >= 3 && a.startsWith(b)) return true;
  return a.split(/[^a-z0-9]+/).filter(Boolean).includes(b);
}

/**
 * "The label on this console names a different deployment than the Manager it
 * is pointing at" — FD-ADR-040 §8's drift condition.
 *
 * This catches the failure that is otherwise COMPLETELY INVISIBLE: a fleet
 * inventory whose entry says `Production` beside a `managerBaseUrl` that is
 * staging's, or a per-environment `config.json` deployed alongside the wrong
 * Manager. Nothing else in the console can see it — every request succeeds,
 * every page renders, and the only thing wrong is the word the operator is
 * reading while they decide whether to abort a wave.
 *
 * A NULL SERVER NAME IS NOT DRIFT. A Manager that does not emit
 * `ENVIRONMENT_NAME` (a local host, an older deployment) has said nothing, and
 * silence must not be rendered as contradiction.
 *
 * Exported because it is the decision, and a decision this consequential is
 * tested directly rather than only through a component.
 *
 * ── ONLY A CONSOLE-SIDE LABEL MAY AGREE (FD-586) ────────────────────────────
 *
 * The check is "does what THIS CONSOLE says match what THE SERVER says". Since
 * FD-586 the label can come from the server itself (`whoami.environmentLabel`,
 * when `config.json` omits it), and letting that value vote would be the server
 * corroborating itself: a fleet entry pointing `apc` at another customer's
 * Manager would resolve that Manager's own label ("Contoso") and its own
 * `ENVIRONMENT_NAME` ("contoso") — and "Contoso" agrees with "contoso", so the
 * banner would stay silent on exactly the misrouting it exists to catch.
 *
 * So a label whose `labelSource` is `server` (or the `id` floor, which is just
 * the id again) is ignored here, and the comparison is decided by the ID — the
 * one console-side identity a label-less config still asserts. The id is what
 * the fleet inventory and the deployment name are generated from, so it is the
 * claim worth testing. An environment with no `labelSource` (a bare config
 * entry, as every caller before FD-586 passed) is treated as config-labelled,
 * which keeps those callers' behaviour unchanged.
 */
export function detectEnvironmentDrift(
  environment: ConsoleEnvironment & { labelSource?: EnvironmentLabelSource },
  serverEnvironmentName: string | null | undefined,
): boolean {
  if (!serverEnvironmentName || serverEnvironmentName.trim() === '') return false;
  const consoleLabel =
    environment.labelSource === undefined || environment.labelSource === 'config'
      ? environment.label
      : undefined;
  return !(agrees(environment.id, serverEnvironmentName) ||
    agrees(consoleLabel, serverEnvironmentName));
}

export interface EnvironmentContextValue {
  /**
   * Every environment this console serves, in config order, labels resolved —
   * INCLUDING the ones hidden from this operator. Lookups by id (a callback page
   * resuming in the environment it started in) need the whole list; anything
   * that RENDERS a choice wants `visibleEnvironments`.
   */
  environments: ResolvedEnvironment[];
  /**
   * The environments this operator may be offered (FD-586): every one not
   * refused with `403 FORBIDDEN_TENANT`. `unknown` stays in — an environment
   * that is down is not one the operator lacks access to.
   */
  visibleEnvironments: ResolvedEnvironment[];
  /**
   * More than one environment is visible, so there is a choice to show. The
   * selector and `EnvironmentEcho` key on this; the URL logic keys on
   * `isSingleEnvironment` (the CONFIGURED count) and must not churn as probes
   * answer.
   */
  hasEnvironmentChoice: boolean;
  /**
   * The active environment is known to be one the routed page may call (FD-586).
   *
   * False while its probe is outstanding, and while it is forbidden and the
   * console is about to land somewhere else — the window in which a page's
   * queries would only collect a refusal. Always true for a single-environment
   * console, so Mode 1 never waits on a probe.
   */
  environmentResolved: boolean;
  /** Every environment has refused this tenant — there is nothing to land on. */
  noAccessibleEnvironment: boolean;
  /** The active one. Never null — `environments` is non-empty by construction. */
  environment: ResolvedEnvironment;
  /** Convenience: `environment.id`. */
  environmentId: string;
  /** Switch. Writes `?env=` and lets the rest fall out of that. */
  setEnvironmentId: (id: string) => void;
  /**
   * An id the URL asked for that this console does not serve, latched until the
   * operator acts on it. See `EnvironmentNotice` for why it is latched rather
   * than derived.
   */
  unknownEnvironmentId: string | null;
  dismissUnknownEnvironment: () => void;
  /**
   * True when the console serves exactly one environment — the Mode 1 shape.
   * `EnvironmentSelector` reads this to render nothing at all.
   */
  isSingleEnvironment: boolean;
  /** Per-environment reachability and reported name. Never missing a key. */
  health: Record<string, EnvironmentHealth>;
  /** Convenience for the active environment — the drift banner's input. */
  activeHealth: EnvironmentHealth;
  /** A probe answered (or refused). Written by `EnvironmentProbe`. */
  recordEnvironmentProbe: (id: string, result: EnvironmentProbeResult) => void;
  /**
   * Stop waiting on these environments: their probe failed for a non-refusal
   * reason, or the settle timeout passed (FD-586). Never overwrites an answer
   * already recorded — a late `forbidden` or `reachable` is the truth, and a
   * timeout that fired first must not unsay it.
   */
  markEnvironmentsSettled: (ids: string[]) => void;
  /**
   * An ordinary request was refused with `403 FORBIDDEN_TENANT`.
   *
   * The second feed into the same state as the probe: the probe pre-hides at
   * boot, and this catches an environment whose access is withdrawn (or whose
   * allow-list changes) while the console is open.
   */
  reportEnvironmentRefusal: (id: string) => void;
}

const EnvironmentContext = createContext<EnvironmentContextValue | null>(null);

/**
 * Read the active environment.
 *
 * Throws outside a provider, deliberately. Returning a tolerant default would
 * mean a component could render, look right, and be addressing an environment
 * nobody chose — the failure this whole module exists to make impossible.
 */
export function useEnvironment(): EnvironmentContextValue {
  const value = useContext(EnvironmentContext);
  if (!value) {
    throw new Error('useEnvironment must be used within an <EnvironmentProvider>.');
  }
  return value;
}

/**
 * Read the active environment, or `null` outside a provider.
 *
 * The tolerant sibling of `useEnvironment`, and it exists for exactly one
 * consumer: `EnvironmentEcho`, which is rendered inside components that many
 * existing tests mount on their own. Those tests are asserting something else
 * entirely, and making every one of them wrap a provider to keep passing would
 * be a large edit whose only purpose is to satisfy a decoration.
 *
 * The obvious objection is right and is answered elsewhere rather than here: an
 * echo that renders nothing outside a provider could silently vanish if the tree
 * were ever restructured. Two things stop that mattering. In the real app the
 * state is unreachable — `App` renders inside the provider, and `api/client.ts`
 * already THROWS by name on a request made outside it, so a console in that
 * shape cannot load a page at all, let alone open a dialog. And
 * `EnvironmentEcho.dialogs.test.tsx` asserts, per dialog and with a provider
 * present, that the environment is named — so the echo disappearing is a red
 * test rather than a quiet regression.
 */
export function useOptionalEnvironment(): EnvironmentContextValue | null {
  return useContext(EnvironmentContext);
}

export interface EnvironmentProviderProps {
  config: ConsoleConfig;
  children: ReactNode;
}

export function EnvironmentProvider({ config, children }: EnvironmentProviderProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const configured = config.environments;

  // The CONFIGURED count, and deliberately not the visible one. It decides the
  // URL behaviour (`?env=` written or not) and whether the landing gate exists
  // at all, and both of those must be fixed facts about this console rather than
  // things that change as probes answer — a console whose address bar sprouted
  // and lost `?env=` as environments were hidden would churn history and
  // invalidate links mid-session. "How many can this operator SEE" is a separate
  // question, answered by `hasEnvironmentChoice` below.
  const isSingleEnvironment = configured.length === 1;

  // FD-422 — per-environment reachability, fed from two places that write the
  // same state: the boot probe (`EnvironmentProbe`) and any ordinary request
  // refused with 403 FORBIDDEN_TENANT (`EnvironmentQueryScope`).
  //
  // Sparse by design. An id with no entry reads as UNKNOWN and UNPROBED, which is
  // the correct answer before a probe has replied.
  const [health, setHealth] = useState<Record<string, EnvironmentHealth>>({});

  // FD-586 — labels resolved ONCE, here. See `ResolvedEnvironment` for why this
  // is not a fallback repeated in each component.
  const environments = useMemo(
    () => configured.map((entry) => resolveEnvironment(entry, health[entry.id])),
    [configured, health],
  );

  const requestedId = searchParams.get(ENV_SEARCH_PARAM);
  const matched = requestedId
    ? environments.find((candidate) => candidate.id === requestedId)
    : undefined;

  // The last environment the URL successfully named.
  //
  // This is NOT a second source of truth — the URL still wins whenever it names
  // a recognised environment, on load and on every navigation. It is a memory of
  // what the URL last said, and it exists because "re-append `?env=`" is
  // otherwise indistinguishable from "reset to the default".
  //
  // Found by the re-append test rather than by reading the code: a page calling
  // `setSearchParams` with a fresh URLSearchParams drops `env=` for one render,
  // and a purely derived active environment falls back to `environments[0]` in
  // that gap. So clicking a tab filter on PRODUCTION moved the operator to
  // staging — silently, including the Manager origin every subsequent request
  // went to. Precisely the hazard FD-ADR-040 §8 is built around, arriving from
  // the mechanism meant to prevent it.
  //
  // React state, deliberately, and never `localStorage`: this dies with the tab
  // and is written straight back into the URL by the effect below, so nothing
  // outlives the session and a link is still the whole truth. The ADR-015 rule
  // bans storage that survives the tab, not memory within it.
  const [selectedId, setSelectedId] = useState<string>(
    () => matched?.id ?? environments[0].id,
  );

  // FD-586 — did a PERSON choose the active environment, or did the console?
  //
  // It decides what happens when the active environment turns out to be one this
  // tenant may not reach. The console's own default (`environments[0]` on a bare
  // URL — on the Air Bridge fleet, Rubix's internal environment) is the console's
  // guess, and correcting a wrong guess silently is just landing well. But an
  // environment someone NAMED — a deep link, the selector, the Back button — was
  // a request, and quietly substituting another deployment for it is the one
  // thing FD-ADR-040 §8 forbids; that case switches AND latches the notice.
  //
  // The console's own landing choice is recorded as implicit too, so a second
  // correction (access withdrawn from where it landed) is also silent: the
  // operator never asked for that environment by name either.
  const [explicitSelection, setExplicitSelection] = useState<boolean>(() => Boolean(matched));

  // `parseConsoleConfig` guarantees a non-empty array — a console with no
  // environment fails at boot — so there is no empty case to handle here. The
  // final `?? environments[0]` covers only a remembered id that a later config
  // no longer lists.
  const environment =
    matched ?? environments.find((candidate) => candidate.id === selectedId) ?? environments[0];

  const [unknownEnvironmentId, setUnknownEnvironmentId] = useState<string | null>(null);

  // Functional updates, so all three callbacks are STABLE. `EnvironmentProbe`
  // has them in its effect dependencies; a callback that changed identity
  // whenever health changed would re-run that effect on its own first result and,
  // without the ref guard there, probe the fleet again per answer.
  const recordEnvironmentProbe = useCallback((id: string, result: EnvironmentProbeResult) => {
    setHealth((current) => ({
      ...current,
      [id]: {
        reachability: result.reachability,
        environmentName: result.environmentName,
        environmentLabel: result.environmentLabel ?? null,
        adminAuthMode: result.adminAuthMode ?? null,
        probed: true,
      },
    }));
  }, []);

  const markEnvironmentsSettled = useCallback((ids: string[]) => {
    setHealth((current) => {
      let next: Record<string, EnvironmentHealth> | null = null;
      for (const id of ids) {
        if (current[id]?.probed) continue;
        next ??= { ...current };
        next[id] = { ...(current[id] ?? UNKNOWN_HEALTH), probed: true };
      }
      // Same object when nothing changed, so a timeout firing after every probe
      // has answered costs no render.
      return next ?? current;
    });
  }, []);

  const reportEnvironmentRefusal = useCallback((id: string) => {
    setHealth((current) => {
      if (current[id]?.reachability === 'forbidden') return current;
      // The environment name recorded by an earlier successful probe is KEPT: a
      // later refusal does not unsay what the Manager already reported, and
      // discarding it would silently clear a drift banner by making the console
      // forget the value it disagreed with. The label likewise.
      return {
        ...current,
        [id]: {
          reachability: 'forbidden',
          environmentName: current[id]?.environmentName ?? null,
          environmentLabel: current[id]?.environmentLabel ?? null,
          adminAuthMode: current[id]?.adminAuthMode ?? null,
          // A refusal IS an answer — the most definite one there is.
          probed: true,
        },
      };
    });
  }, []);

  // ── FD-586: hide, land, and gate ──────────────────────────────────────────
  //
  // A fleet console is one public page in front of many customers' isolated
  // environments. Under FD-422 a customer's admin saw every other customer's
  // environment greyed in the selector, and landed on `environments[0]` —
  // Rubix's own — to be met by "This administrator's tenant is not permitted".
  // Both were the console describing the fleet when the operator asked about
  // THEIR environment.
  //
  // What did not change, and must not: the Manager's tenant allow-list is the
  // security boundary. Everything here is presentation over its verdict; hiding
  // an environment protects nothing, and a hidden environment's Manager still
  // refuses this tenant on every request.
  const isForbidden = (id: string) => health[id]?.reachability === 'forbidden';
  const visibleEnvironments = useMemo(
    () => environments.filter((candidate) => health[candidate.id]?.reachability !== 'forbidden'),
    [environments, health],
  );
  const hasEnvironmentChoice = visibleEnvironments.length > 1;
  // Multi-environment only: Mode 1 keeps FD-422's behaviour exactly, including a
  // page that shows the refusal itself — on a one-environment console there is no
  // "elsewhere" to describe and the page's own error is the honest answer.
  const noAccessibleEnvironment = !isSingleEnvironment && visibleEnvironments.length === 0;
  const activeForbidden = !isSingleEnvironment && isForbidden(environment.id);

  // Where to land when the active environment is forbidden: the FIRST in config
  // order that is confirmed reachable — and "first" is meant strictly. An
  // earlier environment whose probe is still out might yet answer `reachable`, so
  // the walk STOPS there and waits rather than jumping to a later one that
  // happened to answer sooner; landing must not depend on which Manager is
  // warmest. Settled-unknown environments are skipped (we are looking for a
  // confirmed yes), and only when EVERY environment has settled with no yes do we
  // fall back to the first one still visible — an environment that is down is
  // still a better place to stand than one that has refused us, and the operator
  // can see it in the selector. The settle timeout guarantees the walk ends.
  let landingTarget: ResolvedEnvironment | null = null;
  if (activeForbidden) {
    let waiting = false;
    for (const candidate of environments) {
      if (candidate.id === environment.id) continue;
      const candidateHealth = health[candidate.id];
      if (candidateHealth?.reachability === 'reachable') {
        landingTarget = candidate;
        break;
      }
      if (!candidateHealth?.probed) {
        waiting = true;
        break;
      }
    }
    if (!landingTarget && !waiting) landingTarget = visibleEnvironments[0] ?? null;
  }

  const environmentResolved =
    isSingleEnvironment ||
    noAccessibleEnvironment ||
    (Boolean(health[environment.id]?.probed) && !activeForbidden);

  // WRITTEN DURING RENDER, NOT IN AN EFFECT, and that is the whole point.
  //
  // React runs child effects before parent effects. A page's `useQuery` fires
  // from a child effect on mount, so a provider that set the base URL in its own
  // effect would be too late: the first request of every page load would go to
  // whichever environment was set last. Writing it in the render body means the
  // value is correct before any descendant has mounted.
  //
  // Idempotent, so React 18's StrictMode double-render is harmless.
  setActiveManagerBaseUrl(environment.managerBaseUrl);

  // Latch the unrecognised id. It CANNOT be derived from `searchParams`, because
  // the effect below immediately rewrites the URL to the environment actually in
  // use — so a derived notice would be erased in the same breath as the fallback
  // it exists to explain. Latched, it survives until the operator dismisses it or
  // picks an environment.
  useEffect(() => {
    if (requestedId && !matched) {
      setUnknownEnvironmentId(requestedId);
    }
  }, [requestedId, matched]);

  // Adopt what the URL named. A deep link, the Back button, and a pasted address
  // all arrive this way, so the remembered selection follows the URL rather than
  // competing with it — and each of them is somebody naming an environment.
  useEffect(() => {
    if (matched && matched.id !== selectedId) {
      setSelectedId(matched.id);
      setExplicitSelection(true);
    }
  }, [matched, selectedId]);

  // Keep `?env=` on the URL.
  //
  // Two cases, one rule: a load with no `?env=` gets the default written in, and
  // any navigation that replaces the query string (a page setting `?tab=` or
  // `?jobId=` wholesale) gets it put back. `replace: true` so neither adds a
  // history entry — the operator did not navigate, the console tidied up.
  //
  // SUPPRESSED FOR A SINGLE-ENVIRONMENT CONSOLE. With one environment there is
  // nothing to disambiguate, so the parameter is noise on every link a Mode 1
  // operator shares, and FD-ADR-040 §8's byte-identical guarantee is about what
  // an operator sees — the address bar included. Resolution and the unknown
  // notice still run, so `?env=typo` on a single-environment console is still
  // reported rather than ignored.
  useEffect(() => {
    if (isSingleEnvironment) return;
    if (searchParams.get(ENV_SEARCH_PARAM) === environment.id) return;
    const next = new URLSearchParams(searchParams);
    next.set(ENV_SEARCH_PARAM, environment.id);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, environment.id, isSingleEnvironment]);

  // FD-586 — land somewhere this tenant may stand.
  //
  // Declared AFTER the `?env=` keeper on purpose: both may navigate in the same
  // commit (the keeper writing the forbidden default in, this replacing it), and
  // effects run in declaration order, so this one's navigation is the one that
  // sticks. `replace`, because the operator did not navigate — the console
  // corrected a place it should never have put them.
  //
  // A NAMED environment that refuses this tenant latches the existing
  // "That environment is not on this console" notice, and that wording is the
  // decision: it does not say "exists, but you may not see it". To a customer
  // admin, a deep link to another customer's environment and a typo must read
  // identically, or the notice becomes an oracle for which environments a fleet
  // serves.
  const landingTargetId = landingTarget?.id ?? null;
  useEffect(() => {
    if (!landingTargetId || landingTargetId === environment.id) return;
    if (explicitSelection) setUnknownEnvironmentId(environment.id);
    setExplicitSelection(false);
    setSelectedId(landingTargetId);
    const next = new URLSearchParams(searchParams);
    next.set(ENV_SEARCH_PARAM, landingTargetId);
    setSearchParams(next, { replace: true });
  }, [landingTargetId, environment.id, explicitSelection, searchParams, setSearchParams]);

  const setEnvironmentId = useCallback(
    (id: string) => {
      // Never localStorage — the ADR-015 rule ADR-031 §1 restated and this design
      // depends on. A stored environment that outlives the tab is exactly how an
      // operator ends up acting on production believing they are on staging.
      setUnknownEnvironmentId(null);
      setExplicitSelection(true);
      // State first, so the switch is atomic with the render that follows: the
      // Manager base URL and the query scope both move on the same commit as the
      // URL, with no frame in between addressing the old environment.
      setSelectedId(id);
      const next = new URLSearchParams(searchParams);
      next.set(ENV_SEARCH_PARAM, id);
      // NOT `replace` — switching environment is a deliberate act, so Back
      // returns you to where you were, in the environment you were in.
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const dismissUnknownEnvironment = useCallback(() => setUnknownEnvironmentId(null), []);

  const activeHealth = health[environment.id] ?? UNKNOWN_HEALTH;

  const value = useMemo<EnvironmentContextValue>(
    () => ({
      environments,
      visibleEnvironments,
      hasEnvironmentChoice,
      environmentResolved,
      noAccessibleEnvironment,
      environment,
      environmentId: environment.id,
      setEnvironmentId,
      unknownEnvironmentId,
      dismissUnknownEnvironment,
      isSingleEnvironment,
      health,
      activeHealth,
      recordEnvironmentProbe,
      markEnvironmentsSettled,
      reportEnvironmentRefusal,
    }),
    [
      environments,
      visibleEnvironments,
      hasEnvironmentChoice,
      environmentResolved,
      noAccessibleEnvironment,
      environment,
      setEnvironmentId,
      unknownEnvironmentId,
      dismissUnknownEnvironment,
      isSingleEnvironment,
      health,
      activeHealth,
      recordEnvironmentProbe,
      markEnvironmentsSettled,
      reportEnvironmentRefusal,
    ],
  );

  return <EnvironmentContext.Provider value={value}>{children}</EnvironmentContext.Provider>;
}

function createQueryClient(onForbiddenTenant: () => void): QueryClient {
  // FD-422 — the SECOND feed into the reachability state, alongside the boot probe.
  //
  // Cache-level handlers rather than a rule repeated in ~40 query functions: every
  // read and every mutation on this environment passes through here, so an
  // environment whose access is withdrawn mid-session is hidden (and, under FD-586,
  // left) on the next call without anyone having remembered to add a check.
  //
  // `isForbiddenTenantError` is what keeps this narrow. An environment that is
  // merely DOWN must not be presented as one the operator lacks access to — see
  // the predicate for why both the status and the code are required.
  const onError = (error: unknown) => {
    if (isForbiddenTenantError(error)) onForbiddenTenant();
  };

  return new QueryClient({
    // The callback closes over the environment that OWNS this client. A client is
    // built per environment and thrown away on a switch, so a refusal can never be
    // attributed to whichever environment happened to be active when it landed.
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}

/**
 * A query cache scoped to one environment — the defence against cross-environment
 * cache bleed, and the reason the `QueryClient` moved out of `main.tsx`.
 *
 * Two mechanisms, both required, doing different jobs:
 *
 *   - **A fresh `QueryClient` per environment** empties the cache. Without it,
 *     `['migrations']` fetched from staging is served instantly to production —
 *     right key, wrong deployment, and nothing in the key says which.
 *   - **`key={environmentId}`** remounts the subtree, so no component keeps
 *     rendering data it already holds in its own state from the environment it
 *     was mounted under.
 *
 * Throwing the cache away is cheaper and far more certain than invalidating the
 * environment-scoped keys, because it needs no list of which keys those are — and
 * a list is exactly the thing that goes stale the next time someone adds a query.
 * The cost is a refetch on switch, which is the correct thing to do anyway.
 */
export function EnvironmentQueryScope({ children }: { children: ReactNode }) {
  const { environmentId, reportEnvironmentRefusal } = useEnvironment();
  // `environmentId` is the dependency, not an argument — a new cache per
  // environment. `EnvironmentQueryScope` itself does not remount on a switch
  // (the key is on its child), so without this dep the client would survive it.
  //
  // The refusal reporter is captured per client and the id is bound INTO the
  // closure, not read at call time: a 403 that lands after the operator has
  // already switched away belongs to the environment that issued the request.
  const client = useMemo(
    () => createQueryClient(() => reportEnvironmentRefusal(environmentId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [environmentId],
  );

  return (
    <QueryClientProvider key={environmentId} client={client}>
      {children}
    </QueryClientProvider>
  );
}

/**
 * "The URL asked for an environment this console does not serve."
 *
 * Never silent. A console that quietly substituted a different deployment for
 * the one the link named would be doing the single thing FD-ADR-040 §8 is built
 * to prevent, and it would do it at exactly the moment an operator is least
 * likely to check — following a link someone sent them.
 *
 * Dismissible, because it is latched: it reports how you ARRIVED, and that fact
 * does not stop being true when you navigate.
 */
export function EnvironmentNotice() {
  const { unknownEnvironmentId, environment, dismissUnknownEnvironment } = useEnvironment();
  if (!unknownEnvironmentId) return null;

  return (
    <div className="mb-6">
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>That environment is not on this console</MessageBarTitle>{' '}
          The address asked for <span className="font-mono">{unknownEnvironmentId}</span>, which this
          console does not serve. You are working in {environment.label}.
        </MessageBarBody>
        <MessageBarActions
          containerAction={
            <Button
              appearance="transparent"
              icon={<X size={16} strokeWidth={1.5} />}
              aria-label="Dismiss"
              onClick={dismissUnknownEnvironment}
            />
          }
        />
      </MessageBar>
    </div>
  );
}
